import { Entity, EntityApplication, EntityEntitlement, Logger } from '../types';
import type {
  NotificationSettingsResponse,
  UpdateNotificationSettingsPayload,
} from '../pages/Notifications/types';
import type { Favorite } from './favoritesStorage';

export interface UserInfo {
  uuid: string;
  full_name: string;
  first_name: string;
  last_name: string;
  email: string;
  department?: string;
  role?: {
    name: string;
    display_name: string;
  };
}

/**
 * Truncate a response body preview so error messages don't dump an entire
 * HTML page into the UI / logs.
 */
const RESPONSE_BODY_PREVIEW_LENGTH = 200;

function previewBody(text: string): string {
  const cleaned = text.replace(/\s+/g, ' ').trim();
  return cleaned.length > RESPONSE_BODY_PREVIEW_LENGTH
    ? `${cleaned.slice(0, RESPONSE_BODY_PREVIEW_LENGTH)}…`
    : cleaned;
}

export async function authFetch<T>(
  url: string,
  token: string,
  options?: RequestInit,
  logger?: Logger
): Promise<T> {
  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options?.headers,
    },
    credentials: 'include',
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    const errorMessage = `Request failed ${res.status}: ${text || res.statusText}`;

    // Log authentication/authorization failures to Grafana
    if ((res.status === 401 || res.status === 403) && logger) {
      logger.error(errorMessage, {
        type: 'http_auth_error',
        auth_event: res.status === 401 ? 'unauthorized_api_call' : 'forbidden_api_call',
        http_status: res.status,
        url,
        response_preview: text.substring(0, 200),
      });
    }

    throw new Error(errorMessage);
  }

  // Read as text first so we can produce a useful error in any failure case.
  const raw = await res.text();
  const contentType = (res.headers.get('content-type') ?? '').toLowerCase();

  // Reject responses that are explicitly an HTML/markup payload. This catches
  // staging/ingress misconfigurations where the SPA's index.html is served as
  // a fallback (or an auth redirect ends at a 200 HTML login page). Without
  // this guard the consumer would see an unactionable
  // "Unexpected token '<', '<!doctype "... is not valid JSON".
  const isExplicitlyNonJson =
    contentType.includes('text/html') ||
    contentType.includes('application/xml') ||
    contentType.includes('text/xml');
  if (isExplicitlyNonJson) {
    throw new Error(
      `Expected JSON from ${url} but received ${contentType} ` +
        `(status ${res.status}). Body preview: ${previewBody(raw) || '<empty>'}`
    );
  }

  try {
    return JSON.parse(raw) as T;
  } catch {
    throw new Error(
      `Expected JSON from ${url} but body could not be parsed ` +
        `(status ${res.status}, content-type ${contentType || 'unset'}). ` +
        `Body preview: ${previewBody(raw) || '<empty>'}`
    );
  }
}

/**
 * Fetch all entities the user is entitled to access.
 * Proxied via Vite to controlpanel API.
 */
export function fetchAllEntitledEntities(
  userUuid: string,
  token: string,
  logger?: Logger
): Promise<Entity[]> {
  const url = `/api/access/user/v1/${encodeURIComponent(userUuid)}/entities`;
  return authFetch<Entity[]>(url, token, undefined, logger);
}

/**
 * Fetch entitlement data for a specific entity (using friendly id).
 */
export async function fetchEntityEntitlements(
  userUuid: string,
  entityFriendlyId: string,
  token: string,
  logger?: Logger
): Promise<EntityApplication[]> {
  const url = `/api/access/user/v1/${encodeURIComponent(
    userUuid
  )}/entitlements?entityIds=${encodeURIComponent(entityFriendlyId)}&idType=BY_FRIENDLY_ID`;
  const data = await authFetch<EntityEntitlement[]>(url, token, undefined, logger);
  // Only a single entity is expected to be returned, so we return the first item in the array
  return data[0].entitlements;
}

/**
 * Fetch entity-level (user-independent) product entitlements from entity-service's
 * app-switcher lookup. Unlike fetchEntityEntitlements above (per-user,
 * /access/user/v1/{userUuid}/entitlements), the entity's product list is not
 * narrowed by the caller's own grants.
 */
export async function fetchEntityProductEntitlements(
  entityUuid: string,
  token: string,
  logger?: Logger
): Promise<EntityApplication[]> {
  const url = `/api/entity/v1/entitiesForSwitcher:byEntityUuid/${encodeURIComponent(entityUuid)}`;
  const data = await authFetch<{ id: string; entitledProducts: unknown }>(
    url,
    token,
    undefined,
    logger
  );
  // Guard the shape explicitly: an unexpected envelope (renamed/missing field)
  // must throw here so the caller's fail-closed catch resolves to `[]`,
  // rather than silently returning `undefined` and crashing a downstream .map().
  if (!Array.isArray(data.entitledProducts)) {
    throw new Error(
      `Expected entitledProducts array from ${url}, got ${typeof data.entitledProducts}`
    );
  }
  return data.entitledProducts as EntityApplication[];
}

/**
 * Fetch the claim keys (e.g. `PLATFORM_AUDIT__READ__PAYROLL`) the user holds at
 * an entity in one claim domain, from the Platform Policy API. Reading one's own
 * claims needs `PLATFORM_CLAIMS_LIST_OWN`, which every entity user has through
 * BASE_ENTITY_ACCESSOR. Proxied via Vite to controlpanel API.
 */
export async function fetchUserEntityClaims(
  userUuid: string,
  entityUuid: string,
  domain: string,
  token: string,
  options?: { signal?: AbortSignal },
  logger?: Logger
): Promise<string[]> {
  const params = new URLSearchParams({
    subject: `urn:opengov:platform:user/${userUuid}`,
    context: `urn:opengov:platform:entity/${entityUuid}`,
    domain,
  });
  const url = `/api/access/policy/v1/claims/grant?${params.toString()}`;
  const data = await authFetch<{ claims?: unknown }>(
    url,
    token,
    { signal: options?.signal },
    logger
  );
  // Guard the shape so an unexpected envelope throws into the caller's
  // fail-closed handling instead of crashing a downstream lookup.
  if (!Array.isArray(data.claims)) {
    throw new Error(`Expected claims array from ${url}, got ${typeof data.claims}`);
  }
  return data.claims
    .map((claim) => (claim as { key?: unknown }).key)
    .filter((key): key is string => typeof key === 'string');
}

/**
 * Control Panel API wraps settings in a response envelope
 */
interface ControlPanelSettingsResponse {
  user_uuid: string;
  setting_group: string;
  data: NotificationSettingsResponse;
}

/**
 * Fetch notification settings for a user from Control Panel API.
 * GET /api/access/user/v1/{userUuid}/settings/notification-settings
 *
 * Note: API returns settings nested under `data` property
 */
export async function fetchNotificationSettings(
  userUuid: string,
  token: string,
  logger?: Logger
): Promise<NotificationSettingsResponse> {
  const url = `/api/access/user/v1/${encodeURIComponent(userUuid)}/settings/notification-settings`;
  const response = await authFetch<ControlPanelSettingsResponse>(url, token, undefined, logger);
  // Extract the nested data property
  return response.data;
}

/**
 * Update notification settings for a user via Control Panel API.
 * PUT /api/access/user/v1/{userUuid}/settings/notification-settings
 *
 * Note: PUT payload is sent at root level (not wrapped)
 * Note: API response returns settings nested under `data` property
 */
export async function updateNotificationSettings(
  userUuid: string,
  token: string,
  payload: UpdateNotificationSettingsPayload,
  logger?: Logger
): Promise<NotificationSettingsResponse> {
  const url = `/api/access/user/v1/${encodeURIComponent(userUuid)}/settings/notification-settings`;
  // Send payload at root level (API expects: { defaults: ..., overrides: ... })
  const response = await authFetch<ControlPanelSettingsResponse>(
    url,
    token,
    {
      method: 'PUT',
      body: JSON.stringify(payload),
    },
    logger
  );
  // Extract the nested data property from response
  return response.data;
}

/**
 * Fetch user info by UUID from the Control Panel legacy users API.
 * GET /api/v2/users/{userUuid}
 */
export function fetchUserInfo(userUuid: string, token: string, logger?: Logger): Promise<UserInfo> {
  const url = `/api/v2/users/${encodeURIComponent(userUuid)}`;
  return authFetch<UserInfo>(url, token, undefined, logger);
}

export interface FavoritesData {
  favoritesByEntity: Record<string, Favorite[]>;
}

interface FavoritesSettingsResponse {
  user_uuid: string;
  setting_group: string;
  data: FavoritesData;
}

/**
 * Fetch all favorites for a user from Control Panel API.
 * GET /api/access/user/v1/{userUuid}/settings/favorites
 *
 * Treats 404 as empty (settings group not yet created for this user).
 */
export async function fetchFavorites(
  userUuid: string,
  token: string,
  logger?: Logger
): Promise<FavoritesData> {
  const url = `/api/access/user/v1/${encodeURIComponent(userUuid)}/settings/favorites`;
  try {
    const response = await authFetch<FavoritesSettingsResponse>(url, token, undefined, logger);
    return response.data;
  } catch (err) {
    if (err instanceof Error && err.message.startsWith('Request failed 404:')) {
      return { favoritesByEntity: {} };
    }
    throw err;
  }
}

/**
 * Save all favorites for a user to Control Panel API (full replace).
 * PUT /api/access/user/v1/{userUuid}/settings/favorites
 *
 * Must include the complete favoritesByEntity map — all entities, not just the active one.
 */
export async function saveFavorites(
  userUuid: string,
  token: string,
  data: FavoritesData,
  logger?: Logger
): Promise<void> {
  const url = `/api/access/user/v1/${encodeURIComponent(userUuid)}/settings/favorites`;
  await authFetch<FavoritesSettingsResponse>(
    url,
    token,
    { method: 'PUT', body: JSON.stringify(data) },
    logger
  );
}
